import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BillingTransaction } from '@hero/schema';
import { Price } from '../../../app/Price';
import { Badge } from '../../../ui/Badge/Badge';

export interface BillingTransactionsListProps {
  transactions: BillingTransaction[];
  isLoading?: boolean;
}

/**
 * Audit list of settled gateway events for a single Billing — one row
 * per terminal event (paid / failed / refunded). BTx rows are
 * webhook-driven (see `apps/backend/src/app/sales/billing-transaction`),
 * so every row here represents a real gateway interaction.
 */
export function BillingTransactionsList({
  transactions,
  isLoading,
}: BillingTransactionsListProps) {
  const { t } = useTranslation('m-billing');

  if (isLoading) {
    return <div className="h-32 w-full animate-pulse rounded-md bg-gray-100" />;
  }

  if (transactions.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
        {t('billing_doc.empty.title')}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
      <table className="min-w-full divide-y divide-gray-100 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <Th>{t('billing_doc.fields.ref')}</Th>
            <Th>{t('billing_doc.fields.issued_at')}</Th>
            <Th>{t('billing_doc.fields.amount')}</Th>
            <Th>{t('billing_doc.fields.status')}</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {transactions.map(tx => (
            <tr key={String(tx._id)}>
              <Td className="font-mono text-xs text-gray-700">{tx.ref?.main}</Td>
              <Td className="text-gray-500">
                {tx.doc?.createdAt
                  ? new Date(tx.doc.createdAt).toLocaleString()
                  : '—'}
              </Td>
              <Td className="font-medium text-gray-900">
                <Price amount={tx.amount} currency={tx.currency} />
              </Td>
              <Td>
                <TxStatusBadge status={tx.status} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TxStatusBadge({ status }: { status: BillingTransaction['status'] }) {
  const styles: Record<BillingTransaction['status'], string> = {
    success: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    waiting_for_approval: 'bg-blue-100 text-blue-800',
  };
  return (
    <Badge variant="secondary" size="small" className={styles[status]}>
      {status}
    </Badge>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th className="px-4 py-2 text-start text-xs font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </th>
);

const Td = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
